import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { Logo } from '../components/Logo';
import { formatApiError } from '../lib/api';
import { usePageSEO } from '../lib/usePageSEO';

export default function ResetPassword() {
  usePageSEO({
    title: 'Set a new password — MyLeaf Legacy',
    description: 'Choose a new password for your MyLeaf Legacy account.',
    path: '/reset-password',
    noindex: true,
  });
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const nav = useNavigate();

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const tooShort = pw.length > 0 && pw.length < 6;
  const mismatch = pw2.length > 0 && pw !== pw2;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!token) { setErr('This reset link is missing or has expired. Request a new one from the Forgot password page.'); return; }
    if (pw.length < 6) { setErr('Password must be at least 6 characters.'); return; }
    if (pw !== pw2) { setErr('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`,
        { token, password: pw },
        { withCredentials: true },
      );
      setDone(true);
      setTimeout(() => nav('/login', { replace: true }), 2200);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="relative hidden md:flex flex-col justify-between p-10 bg-[hsl(var(--primary))] text-[#FDFBF7] overflow-hidden">
        <Link to="/" className="inline-flex" data-testid="reset-side-logo-link">
          <Logo tone="cream" size={176} />
        </Link>
        <div>
          <div className="ml-overline !text-[#D9B775] !font-semibold">Set a new password</div>
          <h1 className="font-serif text-5xl mt-4 leading-tight font-semibold">Choose something only you would remember.</h1>
          <p className="mt-5 text-[#FDFBF7]/90 max-w-md font-medium">
            After you save it, we'll quietly sign you out everywhere else.
          </p>
        </div>
        <div className="text-xs text-[#FDFBF7]/70 font-medium">© MyLeaf Legacy — Forever Here</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md" data-testid="reset-password-page">
          <div className="md:hidden mb-8"><Link to="/"><Logo size={128} /></Link></div>
          <div className="ml-overline !text-[hsl(var(--primary))] !font-semibold">New password</div>
          <h2 className="font-serif text-4xl mt-2 text-[hsl(var(--primary))] font-semibold">Reset your password.</h2>

          {done ? (
            <div
              className="mt-8 rounded-md border border-[hsl(var(--accent))]/55 bg-[hsl(var(--accent)/0.08)] px-5 py-6"
              data-testid="reset-password-success"
            >
              <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Password updated</h3>
              <p className="mt-3 text-sm text-[hsl(var(--primary)/0.78)] font-medium leading-relaxed">
                Taking you to the sign-in page…
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4 wizard-readable">
              <div>
                <label className="ml-label">New password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--primary)/0.55)]" />
                  <input
                    type={show1 ? 'text' : 'password'} required value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="ml-input pl-11 !pr-10" placeholder="At least 6 characters"
                    data-testid="reset-password-input"
                    minLength={6}
                    autoFocus
                  />
                  <button type="button" onClick={() => setShow1((s) => !s)} className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-[hsl(var(--primary)/0.55)] hover:text-[hsl(var(--primary))] transition-colors" aria-label={show1 ? 'Hide password' : 'Show password'} data-testid="reset-password-toggle" tabIndex={-1}>
                    {show1 ? <EyeOff size={18}/> : <Eye size={18}/>}
                  </button>
                </div>
                {tooShort && <p className="mt-1 text-[12px] text-[hsl(var(--destructive))] font-medium">At least 6 characters.</p>}
              </div>
              <div>
                <label className="ml-label">Confirm new password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--primary)/0.55)]" />
                  <input
                    type={show2 ? 'text' : 'password'} required value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className="ml-input pl-11 !pr-10" placeholder="Type it once more"
                    data-testid="reset-password-confirm"
                    minLength={6}
                  />
                  <button type="button" onClick={() => setShow2((s) => !s)} className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-[hsl(var(--primary)/0.55)] hover:text-[hsl(var(--primary))] transition-colors" aria-label={show2 ? 'Hide password' : 'Show password'} data-testid="reset-password-confirm-toggle" tabIndex={-1}>
                    {show2 ? <EyeOff size={18}/> : <Eye size={18}/>}
                  </button>
                </div>
                {mismatch && <p className="mt-1 text-[12px] text-[hsl(var(--destructive))] font-medium">Passwords do not match.</p>}
              </div>
              {err && <div className="text-sm text-[hsl(var(--destructive))]" data-testid="reset-error">{err}</div>}
              <button
                type="submit"
                disabled={submitting || tooShort || mismatch || !pw || !pw2}
                className="ml-btn-primary w-full !py-3.5"
                data-testid="reset-submit-btn"
              >
                {submitting ? 'Saving…' : 'Save new password'}
              </button>
              <p className="text-sm text-[hsl(var(--primary)/0.78)] font-medium pt-2">
                Need a fresh link? <Link to="/forgot-password" className="text-[hsl(var(--primary))] font-semibold" data-testid="reset-restart-link">Start over →</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
